import Vue from 'vue';
import axios from 'axios';
import VueAxios from 'vue-axios';
import store from '@/services/StoreService';
import queryStringifier from 'qs';
import JwtService from './JwtService';

// Avoid refresh loops
const tries = 2;
let currentTries = 0;

const ApiService = {
    initialize() {
        Vue.use(VueAxios, axios.create({
            paramsSerializer: params => queryStringifier.stringify(params, { encode: false }),
        }));

        Vue.axios.defaults.baseURL = process.env.BACKEND_URL;
    },

    responseIsAuthError(response) {
        if (response && response.status) {
            if (response.status === 401 || response.status === 403) {
                return true;
            }
        }

        return false;
    },

    refreshTokenAndRetry(callback) {
        // Avoid refresh loops
        if (currentTries >= tries) {
            JwtService.clearToken();

            window.location.reload();
            return;
        }

        currentTries += 1;

        store.dispatch('auth/refreshToken')
            .then(() => callback())
            .catch();
    },

    get(path, data, callback, errorCallback, config = {}) {
        Vue.axios.get(path, { params: data })
            .then((response) => {
                callback(response);
            })
            .catch((error) => {
                const { response } = error;

                if (this.responseIsAuthError(response)
                    && !config.overrideAuthRefresh
                ) {
                    this.refreshTokenAndRetry(
                        () => this.get(path, data, callback, errorCallback, config),
                    );
                    return;
                }

                if (errorCallback) {
                    errorCallback(error);
                }
            });
    },
    download(path, headers, data, callback, errorCallback, config = {}) {
        Vue.axios.get(path, { responseType: 'blob', options: { responseType: 'blob' }, headers, params: data })
            .then((response) => {
                callback(response);
            })
            .catch((error) => {
                const { response } = error;

                if (this.responseIsAuthError(response)
                    && !config.overrideAuthRefresh
                ) {
                    this.refreshTokenAndRetry(
                        () => this.get(path, data, callback, errorCallback, config),
                    );
                    return;
                }

                if (errorCallback) {
                    errorCallback(error);
                }
            });
    },
    uploadFile(path, { file, projectId }, callback, errorCallback, config = {}) {
        const formData = new FormData();
        formData.append('file', file, file.name);
        formData.append('projectId', projectId);

        Vue.axios
        .post(path, formData, config)
        .then(response => {
            callback(response);
        })
        .catch((error) => {
            const { response } = error;

            if (this.responseIsAuthError(response)
                && !config.overrideAuthRefresh
            ) {
                this.refreshTokenAndRetry(
                    () => this.remove(path, callback, errorCallback, config),
                );
                return;
            }

            if (errorCallback) {
                errorCallback(error);
            }
        });
    },
    remove(path, callback, errorCallback, config = {}) {
        Vue.axios.delete(path, config)
            .then((response) => {
                callback(response);
            })
            .catch((error) => {
                const { response } = error;

                if (this.responseIsAuthError(response)
                    && !config.overrideAuthRefresh
                ) {
                    this.refreshTokenAndRetry(
                        () => this.remove(path, callback, errorCallback, config),
                    );
                    return;
                }

                if (errorCallback) {
                    errorCallback(error);
                }
            });
    },

    post(path, data, callback, errorCallback, config = {}) {
        Vue.axios.post(path, data, config)
            .then((response) => {
                callback(response);
            })
            .catch((error) => {
                const { response } = error;

                if (this.responseIsAuthError(response)
                    && !config.overrideAuthRefresh
                ) {
                    this.refreshTokenAndRetry(
                        () => this.post(path, data, callback, errorCallback, config),
                    );
                    return;
                }

                if (errorCallback) {
                    errorCallback(error);
                }
            });
    },

    put(path, data, callback, errorCallback, config = {}) {
        Vue.axios.put(path, data, config)
            .then((response) => {
                callback(response);
            })
            .catch((error) => {
                const { response } = error;

                if (this.responseIsAuthError(response)
                    && !config.overrideAuthRefresh
                ) {
                    this.refreshTokenAndRetry(
                        () => this.put(path, data, callback, errorCallback, config),
                    );
                    return;
                }

                if (errorCallback) {
                    errorCallback(error);
                }
            });
    },
};

export default ApiService;



// WEBPACK FOOTER //
// ./src/services/ApiService.js